/* Dreame HK sticky add-to-cart bar (Convx PDP revamp, 2026-09-25)
 *
 * Replaces the theme's original sticky-cart.js. Two things changed:
 *
 *   1. The bar is docked to the BOTTOM and owns its own visibility gate:
 *      it shows whenever the in-page add-to-cart button is NOT on screen,
 *      including at the top of the page before the shopper scrolls, and
 *      hides while the real button is in the readable window (below the
 *      header, above the bar itself). There are therefore never two
 *      add-to-cart targets on screen at once, and never zero.
 *
 *   2. The original element read data-form="" because main-product.liquid
 *      rendered the snippet before product_form_id was assigned. The snippet
 *      is now rendered after </product-info>, so the form resolves. A null
 *      form is still guarded here rather than thrown on.
 *
 * The anchor nav no longer lives inside <sticky-cart>; it is its own element
 * handled by convx-pdp.js.
 *
 * Contract with assets/custom.css (floating rail): while the bar is shown,
 * <html> carries .ab-atc-on and --dhk-atc-h is the bar's measured height, so
 * the WhatsApp / chat / compare / back-to-top rail lifts clear of the bar.
 */
if (!customElements.get('sticky-cart')) {
  customElements.define('sticky-cart',
    class StickyCart extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.form = this.dataset.form ? document.getElementById(this.dataset.form) : null;
        this.mainAtc = this.form ? this.form.querySelector('button[name="add"]') : null;
        this.primaryQuantityInput = this.form ? this.form.querySelector('.quantity__input') : null;
        this.quantityInput = this.querySelector('.quantity__input');
        this.variantButton = this.querySelector('.cx-bar-variant');
        this.variantLabel = this.querySelector('.cx-bar-variant__label');
        this.queued = false;
        this.lastBarH = 0;

        this.onScrollHandler = this.onScroll.bind(this);
        this.applyHandler = this.apply.bind(this);
        window.addEventListener('scroll', this.onScrollHandler, { passive: true });
        window.addEventListener('resize', this.onScrollHandler, { passive: true });
        window.addEventListener('orientationchange', this.onScrollHandler);
        window.addEventListener('load', this.applyHandler);

        if (this.primaryQuantityInput && this.quantityInput) {
          this.onPrimaryQuantityInputChangeHandler = this.onPrimaryQuantityInputChange.bind(this);
          this.primaryQuantityInput.addEventListener('change', this.onPrimaryQuantityInputChangeHandler);
        }

        this.onVariantChangeHandler = this.onVariantChange.bind(this);
        document.addEventListener('afterVariantChanged', this.onVariantChangeHandler);

        if (this.variantButton) {
          this.onClickVariantButtonHandler = this.onClickVariantButton.bind(this);
          this.variantButton.addEventListener('click', this.onClickVariantButtonHandler);
        }

        this.onFocusInHandler = this.onFocusIn.bind(this);
        this.onFocusOutHandler = this.onFocusOut.bind(this);
        this.addEventListener('focusin', this.onFocusInHandler);
        this.addEventListener('focusout', this.onFocusOutHandler);

        // Convx 2026-10-05: while a shopper types in a field elsewhere on the
        // page (the review form, search, newsletter), the on-screen keyboard
        // is up and this fixed bar would float over the field being typed
        // into. Step aside until focus leaves.
        this.typing = false;
        this.onDocFocusInHandler = this.onDocFocusIn.bind(this);
        this.onDocFocusOutHandler = this.onDocFocusOut.bind(this);
        document.addEventListener('focusin', this.onDocFocusInHandler);
        document.addEventListener('focusout', this.onDocFocusOutHandler);

        // A variant change can reflow the bar (longer price, sold-out label)
        // without any scroll, and the rail would then sit on a stale height.
        if (window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(() => this.publishHeight());
          this.resizeObserver.observe(this);
        }

        this.apply();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScrollHandler);
        window.removeEventListener('resize', this.onScrollHandler);
        window.removeEventListener('orientationchange', this.onScrollHandler);
        window.removeEventListener('load', this.applyHandler);
        if (this.primaryQuantityInput && this.onPrimaryQuantityInputChangeHandler) {
          this.primaryQuantityInput.removeEventListener('change', this.onPrimaryQuantityInputChangeHandler);
        }
        document.removeEventListener('afterVariantChanged', this.onVariantChangeHandler);
        if (this.variantButton && this.onClickVariantButtonHandler) {
          this.variantButton.removeEventListener('click', this.onClickVariantButtonHandler);
        }
        this.removeEventListener('focusin', this.onFocusInHandler);
        this.removeEventListener('focusout', this.onFocusOutHandler);
        document.removeEventListener('focusin', this.onDocFocusInHandler);
        document.removeEventListener('focusout', this.onDocFocusOutHandler);
        clearTimeout(this.typingTimer);
        document.documentElement.classList.remove('cx-typing');
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.setOpen(false);
      }

      /* ---- visibility gate ---- */

      headerBottom() {
        const header = document.querySelector('sticky-header header.header, .header-wrapper header.header');
        if (!header) return 0;
        return Math.max(0, header.getBoundingClientRect().bottom);
      }

      shouldShow() {
        // Keep the bar up while the shopper is interacting with it.
        if (this.classList.contains('working')) return true;

        // Keyboard up for a field elsewhere: out of the way.
        if (this.typing) return false;

        // No in-page button to duplicate (product blank): keep the bar so the
        // price and picker stay reachable.
        if (!this.mainAtc) return true;

        const r = this.mainAtc.getBoundingClientRect();
        if (!(r.height || r.width)) return true; // button not rendered

        const vh = window.innerHeight || document.documentElement.clientHeight || 0;
        const barH = this.getBoundingClientRect().height || 0;
        const floor = vh - barH;
        const inPageAtcVisible = r.top < floor && r.bottom > this.headerBottom();
        return !inPageAtcVisible;
      }

      setOpen(show) {
        this.classList.toggle('open', show);
        document.body.classList.toggle('open-sticky-cart', show);
        document.documentElement.classList.toggle('ab-atc-on', show);
      }

      apply() {
        this.queued = false;
        this.setOpen(this.shouldShow());
        this.publishHeight();
      }

      onScroll() {
        if (this.queued) return;
        this.queued = true;
        window.requestAnimationFrame(this.applyHandler);
      }

      // Publishes the bar height for the floating rail in custom.css. Only
      // claimed while the bar is shown, so nothing else on the site moves.
      publishHeight() {
        const root = document.documentElement;
        if (!root.classList.contains('ab-atc-on')) {
          if (this.lastBarH !== 0) {
            this.lastBarH = 0;
            root.style.removeProperty('--dhk-atc-h');
          }
          return;
        }
        const h = Math.round(this.getBoundingClientRect().height);
        if (!h || h === this.lastBarH) return;
        this.lastBarH = h;
        root.style.setProperty('--dhk-atc-h', h + 'px');
      }

      /* ---- interactions ---- */

      onFocusIn() {
        this.classList.add('working');
        this.setOpen(true);
        this.publishHeight();
      }

      onFocusOut() {
        this.classList.remove('working');
        this.onScroll();
      }

      // A field outside the bar that raises the keyboard or the iOS picker.
      isTypingField(el) {
        return !!(el && el.matches && !this.contains(el) && el.matches(
          'input:not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"])' +
          ':not([type="reset"]):not([type="file"]):not([type="hidden"]):not([type="range"])' +
          ':not([type="image"]):not([type="color"]), textarea, select, [contenteditable=""], [contenteditable="true"]'
        ));
      }

      setTyping(on) {
        if (this.typing === on) return;
        this.typing = on;
        // custom.css hides the floating rail (WhatsApp, AI chat, compare,
        // back to top) on the same class, for the same reason.
        document.documentElement.classList.toggle('cx-typing', on);
        this.apply();
      }

      onDocFocusIn(event) {
        if (!this.isTypingField(event.target)) return;
        clearTimeout(this.typingTimer);
        this.setTyping(true);
      }

      // Moving from one field to the next fires focusout then focusin, so wait
      // a beat and look at where focus actually landed.
      onDocFocusOut(event) {
        if (!this.isTypingField(event.target)) return;
        clearTimeout(this.typingTimer);
        this.typingTimer = setTimeout(() => this.setTyping(this.isTypingField(document.activeElement)), 120);
      }

      onPrimaryQuantityInputChange() {
        this.quantityInput.value = this.primaryQuantityInput.value;
      }

      onVariantChange(event) {
        const d = event && event.detail;
        if (!d || !d.variant) return;
        // Fired once by the main section and once by this bar's own
        // product-info sync; both carry the same variant, so either is fine.
        if (this.variantLabel) this.variantLabel.textContent = d.variant.title || '';
        this.onScroll();
      }

      onClickVariantButton(event) {
        const target = event.currentTarget;
        const picker = document.getElementById(target.dataset.variantPickerElement);
        if (!picker) return;
        picker.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        this.classList.remove('working');
      }
    }
  );
}
